import React, { useMemo } from "react";
import { Schedule } from "../../../../models/Schedule.tsx";
import { CommercialLiveTeam } from "./CommercialLiveTeam.tsx";
import { Odd } from "../../../../models/Odd_response_model.tsx";
import { useUser } from "../../../../context/UserContext.tsx";
import { getDrawOddsGroup } from "../../../../helpers/functions.tsx";
import { formatLivePeriodLabel, readLiveScore } from "../../../../helpers/liveScoreFormat.ts";
import { Link } from "react-router-dom";

interface ICommercialLiveComponents {
    game: Schedule;
    liveScoresSynced?: boolean;
    onShowBetsModal: Function;
    setLoadingBet: React.Dispatch<React.SetStateAction<boolean>>;
    loadingBet: boolean;
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
}

export function CommercialLiveComponent(props: ICommercialLiveComponents) {

    const { game, liveScoresSynced = true, onShowBetsModal, loadingBet, setLoadingBet, setErrorMessage } = props;
    const gameTimeDate = new Date(game.start_date ?? '')
    const { user } = useUser();
    if (!user?.userName) {
        throw new Error("User not authenticated");
    }

    const { organizedOdds, theRestOfOdds } = useMemo(() => {
        const matchesTeam = (odd: Odd, teamDisplay?: string) => {
            if (!teamDisplay) return false;
            const target = teamDisplay.trim().toLowerCase();
            return (
                odd.selection?.trim().toLowerCase() === target ||
                odd.name?.trim().toLowerCase() === target
            );
        };

        const groupOddsByMarket = (Object.groupBy(game.odds ?? [], odd => odd.market_id) as { [key: string]: Odd[] }) ?? {};
        const marketsId = Object.keys(groupOddsByMarket);

        const tmpHomeOdds = marketsId.map(marketId => {
            return groupOddsByMarket[marketId].filter(
                o => matchesTeam(o, game.home_team_display) || (o.selection_line === 'under' && o.selection === "")
            );
        }).flat();

        const tmpAwayOdds = marketsId.map(marketId => {
            return groupOddsByMarket[marketId].filter(
                o => matchesTeam(o, game.away_team_display) || (o.selection_line === 'over' && o.selection === "")
            );
        }).flat();

        return {
            organizedOdds: [tmpHomeOdds, tmpAwayOdds] as Odd[][],
            theRestOfOdds: getDrawOddsGroup(game.odds),
        };
    }, [game.odds, game.home_team_display, game.away_team_display]);

    const isAvailable = true;
    const is_live = game.is_live || String(game.status ?? "").toLowerCase() === "live";
    const isGolf = game.sport?.id === 'golf';
    const liveScore = liveScoresSynced
        ? readLiveScore(game)
        : { home: null, away: null, period: null, clock: null };
    const periodLabel = formatLivePeriodLabel(
        game.sport?.id ?? game.sport?.name ?? "",
        game.league?.id ?? game.league?.name ?? "",
        liveScore.period,
        liveScore.clock,
    );

    return (
        <>

            <div className='gamesGroup'>
                <div className='container-time'>
                    <span className="group-text-live group-text-live--period teko">
                        {periodLabel || "Live"}
                    </span>
                    {is_live && !isGolf && (
                        <div className="live">
                            <Link
                                className="props-game-live teko no-link"
                                to={
                                    `props?` +
                                    `id=${encodeURIComponent(game?.id ?? '')}` +
                                    `&league=${encodeURIComponent((game?.league?.id ?? ''))}` +
                                    `&sport=${encodeURIComponent(game?.sport?.id ?? '')}` +
                                    `&home=${encodeURIComponent(game?.home_competitors?.[0]?.name ?? '')}` +
                                    `&away=${encodeURIComponent(game?.away_competitors?.[0]?.name ?? '')}` +
                                    `&homeLogo=${encodeURIComponent(game?.home_competitors?.[0]?.logo ?? '')}` +
                                    `&awayLogo=${encodeURIComponent(game?.away_competitors?.[0]?.logo ?? '')}` +
                                    `&gameId=${encodeURIComponent(game?.game_id ?? '')}` +
                                    `&GameDate=${encodeURIComponent(game?.start_date ?? '')}` +
                                    `&logoLeague=${encodeURIComponent(game?.leagueLogo ?? '')}` +
                                    `&type=${encodeURIComponent('propsLive')}`
                                }
                                onClick={(e) => {
                                    if (user.status !== "Active") {
                                        e.preventDefault();
                                        setErrorMessage("Your account is in read-only mode.");
                                    }
                                }}
                            >
                                More Live Bets +
                            </Link>
                        </div>
                    )}
                </div>

                <div className="pru">
                    {
                        organizedOdds.length > 1 && (
                            <CommercialLiveTeam
                                clock={game?.result?.in_play_data?.clock ?? "00:00"}
                                gameId={game.game_id!}
                                fixture_id={game.id}
                                sport={game.sport.name}
                                gameTime={gameTimeDate}
                                team={game.away_team_display!}
                                league={game.league.name}
                                odds={(organizedOdds.length > 1 ? organizedOdds[1] : [])}
                                onShowBetsModal={onShowBetsModal}
                                gameLogo={game.away_competitors[0]?.logo}
                                opposingTeam={game.home_team_display}
                                is_live={is_live}
                                setLoadingBet={setLoadingBet}
                                loadingBet={loadingBet}
                                showFlag={true}
                                logoLeague={game.leagueLogo}
                                setErrorMessage={setErrorMessage}
                                isAvailable={isAvailable}
                                teamScore={is_live ? liveScore.away : null}

                            />)
                    }

                    {
                        organizedOdds.length > 0 && (
                            <CommercialLiveTeam
                                clock={game?.result?.in_play_data?.clock ?? "00:00"}
                                gameId={game.game_id!}
                                fixture_id={game.id}
                                sport={game.sport.name}
                                gameTime={gameTimeDate}
                                team={game.home_team_display!}
                                league={game.league.name}
                                odds={(organizedOdds.length > 0 ? organizedOdds[0] : [])}
                                onShowBetsModal={onShowBetsModal}
                                gameLogo={game.home_competitors[0]?.logo}
                                opposingTeam={game.away_team_display}
                                showFlag={false}
                                is_live={is_live}
                                setLoadingBet={setLoadingBet}
                                loadingBet={loadingBet}
                                isAvailable={isAvailable}
                                logoLeague={game.leagueLogo}
                                setErrorMessage={setErrorMessage}
                                teamScore={is_live ? liveScore.home : null}

                            />)
                    }

                    {theRestOfOdds.length > 0 &&
                        theRestOfOdds.map((oddsGroup, index) => (
                            <CommercialLiveTeam
                                key={`rest-${index}`}
                                clock={game?.result?.in_play_data?.clock ?? "00:00"}
                                gameId={game.game_id!}
                                fixture_id={game.id}
                                sport={game.sport.name}
                                gameTime={gameTimeDate}
                                team="Draw"
                                league={game.league.name}
                                odds={oddsGroup}
                                onShowBetsModal={onShowBetsModal}
                                gameLogo={""}
                                opposingTeam={game.home_team_display}
                                showFlag={true}
                                is_live={is_live}
                                setLoadingBet={setLoadingBet}
                                loadingBet={loadingBet}
                                isAvailable={isAvailable}
                                logoLeague={game.leagueLogo}
                                setErrorMessage={setErrorMessage}
                            />
                        ))
                    }

                    {(!is_live || (is_live && !isAvailable)) && (
                        <div className="break-message">
                            <div className="break-message-spacer" aria-hidden="true" />
                            <div className="break-message-panel">
                                {isAvailable ? (
                                    <span className="break-message-text">
                                        Available when the
                                        <br />
                                        game starts
                                    </span>
                                ) : (
                                    <span className="break-message-text">
                                        Available During
                                        <br />
                                        Game Breaks
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}